import { CaretLeft } from '@phosphor-icons/react';
import { useLocation, Link, Outlet, matchPath } from 'react-router-dom';

import { AuthAside } from '../auth/AuthAside';
import { AuthBrand } from '../auth/AuthBrand';
import pt from '../../locales/pt';
import { AuthTopBar } from './AuthTopBar';
import { ScrollToTop } from './ScrollToTop';

const AUTH_HEADERS = {
  '/forgot-password': { title: pt.auth.nav.forgotPassword, backTo: '/sign-in' },
  '/reset-password': { title: pt.auth.resetPassword.title, backTo: '/sign-in' },
};

const SPLIT_ROUTES = new Set(['/sign-in', '/sign-up']);

/**
 * Auth shell — .pen Entrar/Registo (split) · Recuperar/Redefinir (TopBar + card).
 */
export function AuthLayout() {
  const { pathname } = useLocation();
  const isSplit = SPLIT_ROUTES.has(pathname);

  if (isSplit) {
    const mode = pathname === '/sign-in' ? 'signIn' : 'signUp';
    return (
      <div className="flex min-h-[100dvh] w-full flex-col bg-background md:grid md:grid-cols-[300px_1fr] lg:grid-cols-[minmax(300px,520px)_1fr]">
        <ScrollToTop />
        <AuthAside mode={mode} className="hidden min-h-[100dvh] md:flex" />
        <div className="relative flex min-h-[100dvh] flex-col px-space-5 py-space-6 short:py-space-3 md:px-space-8 md:py-space-8">
          <div className="flex w-full items-center justify-between">
            <Link
              to="/"
              aria-label={pt.nav.back}
              className="flex size-9 items-center justify-center rounded-ds-full text-foreground transition-colors hover:bg-muted"
            >
              <CaretLeft className="size-[17px]" weight="regular" aria-hidden />
            </Link>
            <AuthBrand to="/" className="md:hidden" />
            <span className="size-9 md:hidden" aria-hidden />
          </div>
          <div className="mx-auto flex w-full max-w-[360px] flex-1 flex-col justify-center gap-space-6 short:gap-space-2 pt-space-6 short:pt-space-1 md:pt-0">
            <Outlet />
          </div>
        </div>
      </div>
    );
  }

  const isReset = Boolean(matchPath('/reset-password/:uid/:token', pathname));
  const header = isReset
    ? AUTH_HEADERS['/reset-password']
    : AUTH_HEADERS[pathname] ?? {
        title: pt.auth.nav.signIn,
        backTo: '/',
      };

  return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-background">
      <ScrollToTop />
      <AuthTopBar title={header.title} backTo={header.backTo} />
      <main className="flex flex-1 flex-col items-center justify-center px-space-5 pb-space-6 pt-[4.5rem]">
        <div className="w-full max-w-[448px]">
          <Outlet />
        </div>
      </main>
      <footer className="flex items-center justify-center gap-space-2 px-space-5 pb-space-5 type-label text-muted-foreground">
        <Link to="/terms" className="hover:text-foreground">
          {pt.landing.footer.terms}
        </Link>
        <span aria-hidden>•</span>
        <Link to="/privacy" className="hover:text-foreground">
          {pt.landing.footer.privacy}
        </Link>
      </footer>
    </div>
  );
}
